// The persistent combat progress panel. A compact strip at the top of the
// canvas that tracks a staged fight from the defend window to completion:
//
//   Defenders → Assignment → First strikes → Effects → Normal strikes → Effects → Complete
//
// The current step is prominent, completed steps stay legible, skipped steps
// are visibly struck. Below the strip, while the fight resolves its effects
// (the large strike modal is off stage and the board is selectable), the
// Deathrites, kill effects and attack effects the fight caused are listed
// with their source portrait, owner and lifecycle status, including declined,
// fizzled and not-automated outcomes. Chained effects sit under their cause.
//
// The panel presents; it never asks. A choice belongs to its effect and is
// answered on the existing choice surface (the effect drawer / board pick),
// so the row only says who is choosing. Nothing here is clickable, and the
// whole panel is transparent to pointer events so the strike modal's
// click-to-skip and the board underneath stay reachable.
//
// Model: `progress` from authCombat.applyCombatProgressFromMirror (steps,
// effects with portraits, the outstanding choice, terminal result).
import { useEffect, useState } from 'preact/hooks';
import { ACCENT_GOLD, TEXTURED_PANEL_STYLE, SUB_PANEL_STYLE, RADIUS } from '../../lib/medievalTheme';
import { k, t } from '../../localization/t';

const Z = 1250;
// How long a completed fight's strip lingers before the panel folds away.
export const COMPLETE_LINGER_MS = 4000;

export const STEPS = [
  { id: 'defenders', label: k('Defenders'), aria: k('Defenders') },
  { id: 'assignment', label: k('Assignment'), aria: k('Assignment') },
  { id: 'firstStrikes', label: k('First strikes'), aria: k('First strikes') },
  { id: 'effectsAfterFirst', label: k('Effects'), aria: k('Effects after first strikes') },
  { id: 'normalStrikes', label: k('Normal strikes'), aria: k('Normal strikes') },
  { id: 'effectsAfterNormal', label: k('Effects'), aria: k('Effects after normal strikes') },
  { id: 'complete', label: k('Complete'), aria: k('Complete') },
];
const STEP_STATUS = {
  pending: k('Pending'),
  active: k('In progress'),
  done: k('Done'),
  skipped: k('Skipped'),
};
export const EFFECT_STATUS_LABEL = {
  waiting: k('Waiting'),
  resolving: k('Resolving'),
  'needs-choice': k('Needs a choice'),
  resolved: k('Resolved'),
  declined: k('Declined'),
  fizzled: k('No effect'),
  unsupported: k('Not automated'),
  manual: k('Resolved by hand'),
  skipped: k('Skipped'),
};
const KIND_LABEL = {
  deathrite: k('Deathrite'),
  kill: k('Kill effect'),
  struck: k('Attacked'),
};
const OUTCOME_LABEL = {
  accept: k('Accepted'),
  decline: k('Declined'),
  declined: k('Declined'),
  manual: k('Resolved by hand'),
  skipped: k('Skipped'),
  fizzled: k('No effect'),
};

const STATUS_COLOR = {
  waiting: 'rgb(150, 132, 100)',
  resolving: ACCENT_GOLD,
  'needs-choice': 'rgb(232, 195, 110)',
  resolved: 'rgb(120, 200, 140)',
  declined: 'rgb(150, 132, 100)',
  fizzled: 'rgb(150, 132, 100)',
  unsupported: 'rgb(232, 96, 96)',
  manual: 'rgb(120, 200, 140)',
  skipped: 'rgb(150, 132, 100)',
};

function reducedMotion() {
  try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches; } catch { return false; }
}

// Whether the panel should be on screen: any unfinished fight, or a finished
// one for a short linger so the final step registers.
export function panelVisible(progress, now = Date.now()) {
  if (!progress) return false;
  if (progress.stage !== 'complete') return true;
  return !progress.completedAt || now - progress.completedAt < COMPLETE_LINGER_MS;
}

// Who is choosing, in the viewer's words. The solo pilot answers every seat.
export function chooserLabel(waitingOn, localSeat, isSolo) {
  if (!waitingOn) return null;
  if (isSolo || waitingOn === localSeat) return t('Awaiting your choice');
  return t('Opponent is choosing');
}

function ownerLabel(controller, localSeat, isSolo) {
  if (isSolo) return controller === 'p1' ? t('Seat 1') : t('Seat 2');
  return controller === localSeat ? t('Yours') : t("Opponent's");
}

function Step({ step, status, active }) {
  const label = t(step.label);
  const aria = `${t(step.aria)}: ${t(STEP_STATUS[status] || STEP_STATUS.pending)}`;
  const color = status === 'active' ? ACCENT_GOLD
    : status === 'done' ? 'rgba(212, 168, 67, 0.75)'
      : 'rgb(120, 108, 84)';
  return (
    <li
      data-step={step.id}
      data-status={status}
      aria-label={aria}
      aria-current={active ? 'step' : undefined}
      title={aria}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.35cqw',
        color,
        fontSize: status === 'active' ? '0.95cqw' : '0.8cqw',
        fontWeight: status === 'active' ? 800 : 600,
        textDecoration: status === 'skipped' ? 'line-through' : 'none',
        opacity: status === 'skipped' ? 0.55 : 1,
        whiteSpace: 'nowrap',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      }}
    >
      <span aria-hidden="true" style={{
        width: '0.55cqw', height: '0.55cqw', borderRadius: '50%',
        backgroundColor: status === 'active' ? ACCENT_GOLD : status === 'done' ? 'rgba(212, 168, 67, 0.7)' : 'transparent',
        boxShadow: status === 'active' ? '0 0 0.5cqw rgba(232, 195, 110, 0.7)' : `inset 0 0 0 1px ${color}`,
      }} />
      <span>{label}</span>
    </li>
  );
}

function EffectRow({ effect, localSeat, isSolo, index, motion, child }) {
  const status = t(EFFECT_STATUS_LABEL[effect.status] || EFFECT_STATUS_LABEL.waiting);
  const chooser = chooserLabel(effect.waitingOn, localSeat, isSolo);
  const outcome = effect.outcome && OUTCOME_LABEL[effect.outcome] && effect.status !== 'needs-choice'
    ? t(OUTCOME_LABEL[effect.outcome]) : null;
  const detail = chooser || (outcome && outcome !== status ? outcome : null);
  const rowLabel = `${effect.name}, ${t(KIND_LABEL[effect.kind] || KIND_LABEL.deathrite)}, ${ownerLabel(effect.controller, localSeat, isSolo)}: ${status}${detail ? `, ${detail}` : ''}`;
  return (
    <li
      data-effect={effect.id}
      data-status={effect.status}
      data-waiting-on={effect.waitingOn || undefined}
      aria-label={rowLabel}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.6cqw',
        padding: '0.3cqw 0.5cqw',
        marginLeft: child ? '1.6cqw' : 0,
        borderLeft: child ? '2px solid rgba(212, 168, 67, 0.35)' : '2px solid transparent',
        opacity: motion ? 0 : 1,
        animation: motion ? `vh-combat-row-in 220ms ease-out ${index * 90}ms forwards` : 'none',
      }}
    >
      <div aria-hidden="true" style={{
        width: '2.2cqw', height: '3cqw', flexShrink: 0,
        borderRadius: RADIUS.card, overflow: 'hidden',
        backgroundColor: '#0e0c08',
        boxShadow: '0 0 0 1px rgba(212, 168, 67, 0.45)',
      }}>
        {effect.imageUrl ? <img src={effect.imageUrl} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: '0.5cqw', alignItems: 'baseline', minWidth: 0 }}>
          <span data-effect-name style={{ color: ACCENT_GOLD, fontWeight: 700, fontSize: '0.9cqw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{effect.name}</span>
          <span style={{ color: 'rgb(150, 132, 100)', fontSize: '0.7cqw', whiteSpace: 'nowrap' }}>
            {t(KIND_LABEL[effect.kind] || KIND_LABEL.deathrite)} · {ownerLabel(effect.controller, localSeat, isSolo)}
          </span>
        </div>
        {effect.description ? (
          <div style={{ color: 'rgb(200, 186, 150)', fontSize: '0.72cqw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{effect.description}</div>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
        <span data-effect-status style={{ color: STATUS_COLOR[effect.status] || ACCENT_GOLD, fontSize: '0.75cqw', fontWeight: 800, whiteSpace: 'nowrap' }}>{status}</span>
        {detail ? <span style={{ color: 'rgb(200, 186, 150)', fontSize: '0.68cqw', whiteSpace: 'nowrap' }}>{detail}</span> : null}
      </div>
    </li>
  );
}

// Order rows so a chained effect follows its cause.
export function orderedEffects(effects) {
  const byParent = new Map();
  for (const e of effects || []) {
    const key = e.parentId || null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(e);
  }
  const out = [];
  const walk = (parentId, depth) => {
    for (const e of byParent.get(parentId) || []) {
      out.push({ effect: e, depth });
      walk(e.id, depth + 1);
    }
  };
  walk(null, 0);
  // An orphan (its parent is not in the list) still renders, at the end.
  for (const e of effects || []) if (!out.some((o) => o.effect.id === e.id)) out.push({ effect: e, depth: 0 });
  return out;
}

export default function CombatProgressPanel({ progress, localSeat, isSolo = false, modalUp = false, now }) {
  const [, tick] = useState(0);
  const completedAt = progress?.stage === 'complete' ? progress.completedAt : null;
  useEffect(() => {
    if (!completedAt) return undefined;
    const remaining = Math.max(0, COMPLETE_LINGER_MS - ((now ?? Date.now()) - completedAt));
    const timer = setTimeout(() => tick((n) => n + 1), remaining + 20);
    return () => clearTimeout(timer);
  }, [completedAt]);
  if (!panelVisible(progress, now ?? Date.now())) return null;

  const motion = !reducedMotion();
  const activeStep = STEPS.find((s) => s.id === progress.stage);
  const rows = orderedEffects(progress.effects);
  const showEffects = rows.length > 0 && !modalUp;
  const chooser = chooserLabel(progress.choice?.seat, localSeat, isSolo);
  const announcement = activeStep
    ? `${t(activeStep.aria)}${progress.terminal ? `. ${t('The fight ended the game.')}` : ''}${chooser ? `. ${chooser}` : ''}`
    : '';

  return (
    <section
      data-combat-progress
      data-stage={progress.stage}
      aria-label={t('Combat progress')}
      style={{
        position: 'fixed', top: '0.35cqw', left: '50%', transform: 'translateX(-50%)',
        zIndex: Z,
        pointerEvents: 'none',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4cqw',
        maxWidth: '62cqw',
      }}
    >
      <style>{'@keyframes vh-combat-row-in { from { opacity: 0; transform: translateY(-0.3cqw); } to { opacity: 1; transform: none; } }'}</style>
      <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{announcement}</span>
      <ol
        aria-label={t('Combat steps')}
        style={{
          ...TEXTURED_PANEL_STYLE,
          borderRadius: RADIUS.lg,
          listStyle: 'none', margin: 0,
          padding: '0.45cqw 1.1cqw',
          display: 'flex', alignItems: 'center', gap: '1.1cqw',
        }}
      >
        {STEPS.map((step) => {
          const status = progress.steps.find((s) => s.id === step.id)?.status || 'pending';
          return <Step key={step.id} step={step} status={status} active={step.id === progress.stage} />;
        })}
      </ol>
      {showEffects ? (
        <ol
          aria-label={t('Combat effects')}
          style={{
            ...SUB_PANEL_STYLE,
            borderRadius: RADIUS.lg,
            listStyle: 'none', margin: 0,
            padding: '0.4cqw 0.6cqw',
            display: 'flex', flexDirection: 'column', gap: '0.15cqw',
            minWidth: '30cqw', maxWidth: '44cqw',
            maxHeight: '18cqw', overflow: 'hidden',
          }}
        >
          {rows.map(({ effect, depth }, i) => (
            <EffectRow key={effect.id} effect={effect} localSeat={localSeat} isSolo={isSolo} index={i} motion={motion} child={depth > 0} />
          ))}
        </ol>
      ) : null}
      {progress.terminal ? (
        <div data-combat-terminal style={{ color: ACCENT_GOLD, fontSize: '0.85cqw', fontWeight: 800, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
          {t('The fight ended the game.')}
        </div>
      ) : null}
    </section>
  );
}
